import { ReactNode, createContext, useContext, useReducer } from "react";
import { ErrorDetailContainer } from "../../../api/config";
import { RegistrationRequestIntroductionMethod } from "../../../api/psc";

//Defining Registration Context Type
export type RegistrationContextType = {
  state: RegistrationStateType;
  dispatch: React.Dispatch<RegistrationActionType>;
};

// Initializing Context
const RegistrationContext = createContext({} as RegistrationContextType);

// Creating custom hook for using context
export const useRegistrationContext = () => {
  return useContext(RegistrationContext);
};

// State which context is responsible for
export type RegistrationStateType = {
  fullName: string;
  contactNumber: string;
  companyName: string;
  introductionMethod: RegistrationRequestIntroductionMethod | null;
  lineOfWork: string;
  otpCode: string | null;
  captcha: string | null;
  requestInProgress: boolean;
  error: ErrorDetailContainer | null;
  registrationCode: string;
  diactivateSocial: boolean;
  city: string;
};

const initialState: RegistrationStateType = {
  fullName: "",
  contactNumber: "",
  companyName: "",
  introductionMethod: null,
  lineOfWork: "",
  otpCode: null,
  captcha: null,
  requestInProgress: false,
  error: null,
  registrationCode: "",
  diactivateSocial: false,
  city: "",
};

// Defining Action Types
export type RegistrationActionType =
  | {
      type: "setFullName";
      payload: string;
    }
  | {
      type: "setContactNumber";
      payload: string;
    }
  | {
      type: "setCompanyName";
      payload: string;
    }
  | {
      type: "setIntroductionMethod";
      payload: RegistrationRequestIntroductionMethod;
    }
  | {
      type: "setLineOfWork";
      payload: string;
    }
  | {
      type: "setCity";
      payload: string;
    }
  | {
      type: "setOtpCode";
      payload: string;
    }
  | {
      type: "setCaptcha";
      payload: string;
    }
  | {
      type: "setRequestInProgress";
      payload: boolean;
    }
  | {
      type: "setError";
      payload: ErrorDetailContainer;
    }
  | {
      type: "clearError";
    }
  | {
      type: "setRegistrationCode";
      payload: string;
    }
  | {
      type: "diactivateSocial";
      payload: boolean;
    };

// Defining Reducers
const reducer = (
  state: RegistrationStateType,
  action: RegistrationActionType
) => {
  switch (action.type) {
    case "setFullName":
      return { ...state, fullName: action.payload };
    case "setContactNumber":
      return { ...state, contactNumber: action.payload };
    case "setCompanyName":
      return { ...state, companyName: action.payload };
    case "setIntroductionMethod":
      return {
        ...state,
        introductionMethod: action.payload,
      };
    case "setLineOfWork":
      return { ...state, lineOfWork: action.payload };
    case "setOtpCode":
      return { ...state, otpCode: action.payload };
    case "setCaptcha":
      return { ...state, captcha: action.payload };
    case "setRequestInProgress":
      return { ...state, requestInProgress: action.payload };
    case "setError":
      return { ...state, error: action.payload };
    case "clearError":
      return { ...state, error: null };
    case "setRegistrationCode":
      return { ...state, registrationCode: action.payload };
    case "setCity":
      return { ...state, city: action.payload };
    case "diactivateSocial":
      return { ...state, diactivateSocial: action.payload };
    default:
      return { ...state };
  }
};

// Creating Provider and states for the context
export default function RegistrationContextProvider(props: {
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <RegistrationContext.Provider value={{ state, dispatch }}>
      {props.children}
    </RegistrationContext.Provider>
  );
}
